import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { fabric } from 'fabric';
import { RealtimeSpaceService } from 'src/app/services/realtime-space.service';
import { Mouse, Point } from 'src/app/models/session-sync';
import { combineLatest, filter, map, Subscription, switchMap } from 'rxjs';
import { SessionRtdService } from 'src/app/services/session-rtd.service';
import { throttle, DebouncedFunc } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { PresenceProvider } from 'src/app/services/presence-provider';
import { RedisPresenceV2 } from 'src/app/services/presence_v2.service';
import { UserAction } from 'src/app/common/utils/presence.util';
import { enterZone, modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { SpaceRepository } from 'src/app/state/space.repository';
import { filterNil } from '@ngneat/elf';
import { CursorsControl } from 'src/app/models/session';
import { UserViewportService } from 'src/app/services/user-viewport.service';
import {
  ActiveCanvasStateService,
  ActiveCanvasType,
} from 'src/app/services/active-canvas-state.service';
import { SessionSharedDataService } from '../../../services/session-shared-data.service';
import {
  cursorTooltipWidth,
  cursorTooltipHeight,
  customPointerOffset,
  customPointerOffsetLeft,
  customPointerOffsetTop,
  pointerCaptureRate,
  pointerOffsetLeft,
  pointerOffsetTop,
  RealTimePointerHandler,
  CURSORS_CONTROL_AUTOMATIC_LIMIT,
} from '../wb-toolbar/toolbars/realtime-common';
import { SessionsVptService } from '../../../services/sessions-vpt.service';
import { SpaceBoardsService } from '../../../services/space-boards.service';

/*
TODO
- benchmarking
*/

export enum OverlayObjectType {
  PATH = 'path',
  POINTER = 'pointer',
}

export class OverlayObject extends fabric.Object {
  userId?: string;
  type?: OverlayObjectType;
  timestamp?: number;
  uid?: string;
}
export class OverlayCanvas extends fabric.StaticCanvas {
  private getOverlayObjects(): OverlayObject[] {
    const objects = this.getObjects();
    return objects as OverlayObject[];
  }

  public findOverlayObjects(
    type: OverlayObjectType,
    userId?: string,
    uid?: string,
  ): OverlayObject[] {
    const objects = this.getOverlayObjects();
    const matchingObjects = objects.filter(
      (obj) => obj.type === type && (!userId || obj.userId === userId) && (!uid || obj.uid === uid),
    );
    return matchingObjects;
  }

  public setMetadata(
    obj: OverlayObject,
    userId: string,
    type: OverlayObjectType,
    timestamp: number,
    uid: string,
  ): void {
    obj.type = type;
    obj.userId = userId;
    obj.timestamp = timestamp;
    obj.uid = uid;
  }
}

export class OverlayMetadata {
  // Current points for the RTD path
  currentLine: Point[];

  // Current color for the pointer
  pointerColor?: string;

  // svg pointer
  svgPointer?: string;

  constructor() {
    this.currentLine = [];
  }
}

export interface RtEvent {
  metadata: OverlayMetadata;
  event: {
    mouse: Mouse;
    user: string;
    activeToolbar?: string;
    toolbarChanged?: boolean;
  };
}

@UntilDestroy()
@Component({
  selector: 'app-overlay-canvas',
  templateUrl: './overlay-canvas.component.html',
  styleUrls: ['./overlay-canvas.component.scss'],
})
export class OverlayCanvasComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  userMetadata: { [key: string]: OverlayMetadata } = {};
  lastVptValue: number[] = [];
  public canvas?: OverlayCanvas;
  renderFunc?: DebouncedFunc<() => void>;

  private viewportSubscription?: Subscription;
  domListener = this.domListenerFactoryService.createInstance();
  private lastHiddenPointers: string[] = [];
  private notificationFired = false;
  private isOverlayActive = false;

  constructor(
    private realtimeSpaceService: RealtimeSpaceService,
    private sessionSharedDataService: SessionSharedDataService,
    private sessionRtdService: SessionRtdService,
    private sessionsVptService: SessionsVptService,
    private spaceBoardsService: SpaceBoardsService,
    private domListenerFactoryService: DomListenerFactoryService,
    private presenceProvider: PresenceProvider,
    private spaceRepo: SpaceRepository,
    private redisPresenceV2: RedisPresenceV2,
    private zone: NgZone,
    private userViewportService: UserViewportService,
    private activeCanvasStateService: ActiveCanvasStateService,
  ) {}

  private controlPointersVisibility$ = this.spaceRepo.activeSpaceSelectedBoardUid$
    .pipe(filterNil())
    .pipe(
      switchMap((spaceId) =>
        combineLatest([
          this.redisPresenceV2.framePresenceStatus$.pipe(
            map((presenceActivity) =>
              (presenceActivity[spaceId] || []).map((id) => id.split('-')[0]),
            ),
          ),
          this.spaceRepo.spaceCursorsPermission$,
          this.spaceRepo.spaceHostUserIds$(),
        ]),
      ),
    );

  ngOnInit() {
    this.setupResetHandlers();
    this.domListener.add(window, 'resize', () => {
      this.resizeCanvases();
    });

    this.setupViewportHandler();

    this.controlPointersVisibility$
      .pipe(enterZone(this.zone), untilDestroyed(this))
      .subscribe(([users, cursorsPermission, hosts]) => {
        const usersLengthExceedingLimit = users.length > CURSORS_CONTROL_AUTOMATIC_LIMIT;
        const participants = users.filter((user) => !hosts.has(user));

        /**
         * the togglePointers method is used to hide the entire canvas
         * the toggleSpecificPointers method is used to hide specific pointers only
         * so its important to recover canvas visibility when permission is not HIDE_ALL
         */

        switch (cursorsPermission) {
          case CursorsControl.HIDE_ALL:
            this.sessionRtdService.togglePointorsViaPermissions(true);
            break;
          case CursorsControl.SHOW_ALL:
            this.restoreHiddenPointers();
            this.sessionRtdService.togglePointorsViaPermissions(false);
            break;
          case CursorsControl.AUTOMATIC:
            this.sessionRtdService.togglePointorsViaPermissions(false);
            // when set to automatic and count of users > limit: apply the hosts only
            if (usersLengthExceedingLimit) {
              this.toggleHostOnlyPointers(Array.from(hosts), participants);
              if (!this.notificationFired) {
                this.sessionRtdService.showCursorNotification();
                this.notificationFired = true;
              }
            } else {
              this.restoreHiddenPointers();
            }
            break;
          case CursorsControl.ONLY_HOST:
            this.sessionRtdService.togglePointorsViaPermissions(false);
            this.toggleHostOnlyPointers(Array.from(hosts), participants);
            break;
        }
      });
  }

  private restoreHiddenPointers() {
    if (this.lastHiddenPointers.length > 0) {
      this.sessionRtdService.toggleSpecificPointers(this.lastHiddenPointers, false);
      this.lastHiddenPointers = [];
    }
  }

  private toggleHostOnlyPointers(hosts: string[], participants: string[]) {
    this.lastHiddenPointers = participants;
    this.sessionRtdService.toggleSpecificPointers(participants, true);
    this.sessionRtdService.toggleSpecificPointers(hosts, false);
  }

  ngOnDestroy() {
    this.viewportSubscription?.unsubscribe();
    this.domListener.clear();
    this.canvas?.dispose();
    if (this.sessionRtdService.overlayCanvas === this.canvas) {
      this.sessionRtdService.clearOverlayCanvas();
    }
    this.renderFunc = undefined;
  }

  resizeCanvases() {
    const size = this.getViewportSize();
    this.canvas?.setDimensions({ width: size.width, height: size.height });

    // Safari/iPadOS may report delayed sizes; apply double-pass updates
    modifiedSetTimeout(
      () => {
        const lateSize = this.getViewportSize();
        this.canvas?.setDimensions({ width: lateSize.width, height: lateSize.height });
      },
      100,
      true,
    );
  }

  private getViewportSize(): { width: number; height: number } {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  private setupViewportHandler() {
    this.viewportSubscription?.unsubscribe();

    this.viewportSubscription = this.sessionsVptService.viewportTransform
      .pipe(untilDestroyed(this))
      .subscribe((vpt) => {
        if (!vpt) {
          return;
        }
        this.handleViewportTransformChange(vpt);
        this.changeSVGPointerTransform(vpt);
      });

    const vpt = this.sessionsVptService.viewportTransform.getValue();
    if (vpt) {
      this.handleViewportTransformChange(vpt);
    }
  }

  private getSvgContainer(): HTMLElement | null {
    const el = document.getElementById('overlay-canvas-svg-pointer');
    return el;
  }

  private changeSVGPointerTransform(vpt: number[] | null) {
    const el = this.getSvgContainer();
    if (!el || !vpt) {
      return;
    }

    el.style.transform = `matrix(${vpt.toString()})`;
  }

  // Gets the inverse of the zoom factor from the canvas
  private getInvZoomScale(vpt: number[]): { scaleX: number; scaleY: number } {
    const invVpt = fabric.util.invertTransform(vpt);
    const scale = (({ scaleX, scaleY }) => ({ scaleX, scaleY }))(fabric.util.qrDecompose(invVpt));

    return scale;
  }

  // scales the pointer and updates the offset
  private setScale(pointer: HTMLElement, scale: number) {
    // Find the arrow <svg> directly rather than by child position: the AGENT cursor
    // also has a ripple wrapper (<div>) inserted as an earlier sibling, so
    // firstElementChild is NOT the arrow for Luna. querySelector keeps the arrow
    // inverse-zoom-rescaled on every zoom regardless of sibling order (previously
    // the agent arrow drifted with zoom because setScale mutated the ripple wrapper).
    const svg = pointer.querySelector('svg') as HTMLElement | null;
    const g = svg?.firstElementChild;

    // Keep the agent's landing ripple the same constant SCREEN size as the arrow by
    // applying the identical inverse-zoom scale to its ring holder (no-op for user
    // cursors, which have no ripple).
    const ringHolder = pointer.querySelector('.agent-ripple-rings') as HTMLElement | null;
    if (ringHolder) {
      ringHolder.style.transform = `scale(${scale})`;
    }

    if (!g || !svg) {
      return;
    }
    g.setAttribute('transform', `scale(${scale})`);

    const activeToolbar = pointer.getAttribute('activeToolbar') as string;
    const topOffset = customPointerOffset.includes(activeToolbar)
      ? customPointerOffsetTop
      : pointerOffsetTop;
    const letOffset = customPointerOffset.includes(activeToolbar)
      ? customPointerOffsetLeft
      : pointerOffsetLeft;

    svg.style.top = `${-topOffset * scale}`;
    svg.style.left = `${-letOffset * scale}`;
    svg.style.width = `${cursorTooltipWidth * scale}px`;
    svg.style.height = `${cursorTooltipHeight * scale}px`;
  }

  private handleViewportTransformChange(vpt: number[]) {
    // Handle the scaling
    const { scaleX } = this.getInvZoomScale(vpt);

    // in case last received VPT values and the containers doesn't contain the children cursors
    // we should save so that use it to set the correct style and scale && width values for the cursors
    if (!this.getPointerContainer().length) {
      this.lastVptValue = vpt;
    } else {
      this.lastVptValue = [];
      this.getPointerContainer().forEach((pointer) => {
        this.setScale(pointer, scaleX);
      });
    }

    this.setTransformation(this.canvas, vpt);
    this.changeSVGPointerTransform(vpt);
  }

  private setTransformation(canvas: undefined | fabric.StaticCanvas, vpt: number[]): void {
    canvas?.setViewportTransform(vpt);
    if (this.renderFunc) {
      this.renderFunc();
    }
  }

  // Gets the container for the pointers
  private getPointerContainer(): HTMLElement[] {
    const el = this.getSvgContainer();
    if (!el) {
      return [];
    }

    const children = el.children;
    return Array.from(children) as HTMLElement[];
  }

  // Gets the SVG for the pointer from the dom
  // Note: there is a container for each pointer
  getPointer(id: string): HTMLElement | null {
    const containerId = RealTimePointerHandler.SVGIdFormat(id);
    const container = document.getElementById(containerId);
    return container;
  }

  setupResetHandlers() {
    this.setupFrameChangeResetHandler();

    this.sessionSharedDataService.sessionChanged.pipe(untilDestroyed(this)).subscribe(() => {
      const users = Object.keys(this.userMetadata);
      modifiedSetTimeout(() => users.forEach((id) => this.cleanupUser(id)), 2 * pointerCaptureRate);
    });

    this.realtimeSpaceService.service.usersLeftFrame$
      .pipe(untilDestroyed(this))
      .subscribe((users) => {
        modifiedSetTimeout(
          () => users.forEach((userId) => this.cleanupUser(userId)),
          2 * pointerCaptureRate,
        );
      });

    this.presenceProvider
      .getParticipantPresenceStatus()
      .pipe(untilDestroyed(this))
      .subscribe((participantPresenceStatus) => {
        if (participantPresenceStatus.userAction === UserAction.LEAVE_SPACE) {
          modifiedSetTimeout(
            () => this.cleanupUser(participantPresenceStatus.userId),
            2 * pointerCaptureRate,
          );
        }
      });
  }

  setupFrameChangeResetHandler() {
    // cleanup user pointers when selected board changes
    this.spaceBoardsService.activeSpaceSelectedBoard$.pipe(untilDestroyed(this)).subscribe(() => {
      const users = Object.keys(this.userMetadata);
      modifiedSetTimeout(() => {
        users.forEach((id) => {
          // clean only users pointers not included in the current frame
          if (!this.sessionSharedDataService.currentFrameHasUser(id)) {
            this.cleanupUser(id);
          }
        });
      }, 2 * pointerCaptureRate);
    });
  }

  cleanupUser(userId: string) {
    if (!(userId in this.userMetadata)) {
      return;
    }

    // Remove the realtime path from the canvas
    const paths = this.canvas?.findOverlayObjects(OverlayObjectType.PATH, userId);
    this.userViewportService.removeViewportBoxForUser(userId);

    if (paths) {
      for (const path of paths) {
        this.canvas?.remove(path);
      }
    }

    // Remove the pointer from the DOM
    const pointer = this.getPointer(userId);
    pointer?.parentElement?.remove();
  }

  private setupCanvas() {
    this.canvas = new OverlayCanvas('overlay-canvas', {
      width: window.innerWidth,
      height: window.innerHeight,
    });

    // Set upper limit on the number of renders / sec
    this.renderFunc = throttle(this.canvas.requestRenderAll.bind(this.canvas), pointerCaptureRate); // 12 fps
  }

  ngAfterViewInit() {
    this.setupCanvas();

    this.activeCanvasStateService.activeCanvasType$
      .pipe(untilDestroyed(this))
      .subscribe((activeCanvasType) => {
        this.setOverlayCanvasActive(activeCanvasType === ActiveCanvasType.WHITEBOARD);
      });

    this.realtimeSpaceService.service.realTimeEvent$
      .pipe(
        filter(
          () =>
            !!this.canvas &&
            !!this.renderFunc &&
            this.isOverlayActive &&
            this.sessionRtdService.overlayCanvas === this.canvas,
        ),
        untilDestroyed(this),
      )
      .subscribe((events) => {
        const rtEvents: RtEvent[] = [];
        for (const event of events) {
          const eventCanvasType = event.mouse.metadata?.canvasType ?? ActiveCanvasType.WHITEBOARD;
          if (eventCanvasType !== ActiveCanvasType.WHITEBOARD) {
            continue;
          }
          const id = event.user;

          if (!this.userMetadata[id]) {
            // Create the metadata object for realtime
            this.userMetadata[id] = new OverlayMetadata();
          }
          const metadata = this.userMetadata[id];
          rtEvents.push({ metadata, event });
        }

        this.sessionRtdService.rtEventEmitter.emit(rtEvents);
      });
  }

  ngAfterViewChecked(): void {
    if (this.getPointerContainer().length && this.lastVptValue.length) {
      this.handleViewportTransformChange(this.lastVptValue);
      this.lastVptValue = [];
    }
  }

  private setOverlayCanvasActive(isActive: boolean): void {
    if (!this.canvas || !this.renderFunc) {
      return;
    }

    if (isActive) {
      if (!this.isOverlayActive) {
        this.sessionRtdService.setOverlayCanvas(this.canvas, this.renderFunc);
        this.isOverlayActive = true;
      }
      return;
    }

    if (this.isOverlayActive && this.sessionRtdService.overlayCanvas === this.canvas) {
      this.sessionRtdService.clearOverlayCanvas();
    }
    this.isOverlayActive = false;
  }
}
